import {
  Card,
  Tab,
  Table,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  Tabs
} from "@material-ui/core";
import { useToggleAutobooking } from "@saleor/autobooking/mutations";
import { useTableStyles } from "@saleor/autobooking/styles/tableStyles";
import {
  ExpressFleetMallsConfig_malls_edges_node,
  ExpressFleetMallsConfigVariables
} from "@saleor/autobooking/types/ExpressFleetMallsConfig";
import {
  ExpressFleetListUrlQueryParams,
  expressFleetUrl
} from "@saleor/autobooking/urls";
import ButtonActions from "@saleor/components/ButtonActions";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import { DEFAULT_INITIAL_PAGINATION_DATA } from "@saleor/config";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { ListProps } from "@saleor/types";
import { StoreTypeEnum } from "@saleor/types/globalTypes";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";

import AutoBookingDialog from "../AutoBookingDialog";
import MallsDialog from "../MallsDialog";
import MallSearchInput from "../MallsDialog/components/MallSearchInput";
import TableBodyContent from "./TableBodyContent";

interface ExpressFleetMallsTableProps extends ListProps {
  params: ExpressFleetListUrlQueryParams;
  mallList: ExpressFleetMallsConfig_malls_edges_node[];
  isLoading: boolean;
  refetch: (variables?: ExpressFleetMallsConfigVariables) => any;
  userType: any;
  activeTab: string;
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
}

const numberOfColumns = 9;
const btnNoOfColumns = 4;

const ExpressFleetMallsTable: React.FC<ExpressFleetMallsTableProps> = props => {
  const {
    params,
    mallList,
    isLoading,
    refetch,
    userType,
    activeTab,
    setActiveTab,
    disabled,
    settings,
    pageInfo,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings
  } = props;
  const notify = useNotifier();
  const navigate = useNavigator();
  const tblClasses = useTableStyles({ props });

  const [openModal, setOpenModal] = useState(false);
  const [openDisableModal, setOpenDisableModal] = useState(false);
  const [openEnableModal, setOpenEnableModal] = useState(false);
  const [searchText, setSearchText] = React.useState(params?.search || "");

  const [selectedMall, setSelectedMall] = React.useState<
    ExpressFleetMallsConfig_malls_edges_node
  >(null);

  const mallsDialogTitle =
    activeTab === "fnb" ? "Food & Beverage" : "General Merchandise";

  const storeType =
    activeTab === "fnb"
      ? StoreTypeEnum.FOOD_AND_BEVERAGES
      : StoreTypeEnum.GENERAL_MERCHANDISE;

  const closeModal = () => {
    setSelectedMall(null);
    setOpenModal(false);
  };

  const closeEnableModal = () => {
    setOpenEnableModal(false);
  };

  const closeDisableModal = () => {
    setOpenDisableModal(false);
  };

  const handleConfirmEnableAutobooking = (mallIds: string[]) => {
    toggleMalls({
      variables: {
        input: {
          enabled: true,
          storeTypes: [storeType]
        },
        mallIds
      }
    });
  };

  const handleConfirmDisableAutobooking = (mallIds: string[]) => {
    toggleMalls({
      variables: {
        input: {
          enabled: false,
          storeTypes: [storeType]
        },
        mallIds
      }
    });
  };

  const [toggleMalls] = useToggleAutobooking({
    onCompleted: data => {
      if (data?.autoBookingSetIsEnabled?.autoBookingError?.length === 0) {
        notify({
          status: "success",
          text: "Saved Changes"
        });
        refetch();
        closeDisableModal();
        closeEnableModal();
      } else if (data?.autoBookingSetIsEnabled?.autoBookingError?.length > 0) {
        data.autoBookingSetIsEnabled.autoBookingError.forEach(error => {
          notify({
            status: "error",
            text: error.message
          });
        });
      }
    }
  });

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
  };

  const onSearchText = (text: string) => {
    setSearchText(text);
    navigate(
      expressFleetUrl({
        search: text,
        ...DEFAULT_INITIAL_PAGINATION_DATA
      }),
      true
    );
    refetch();
  };

  return (
    <>
      <Card>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="on"
          className={tblClasses.customTabs}
          indicatorColor="primary"
        >
          <Tab
            className={
              activeTab === "fnb"
                ? tblClasses.activeTab
                : tblClasses.inactiveTab
            }
            value="fnb"
            label="Food & Beverage"
          />
          <Tab
            className={
              activeTab === "gm" ? tblClasses.activeTab : tblClasses.inactiveTab
            }
            value="gm"
            label="General Merchandise"
          />
        </Tabs>

        <Table>
          <TableHead>
            <TableRow className={tblClasses.tableCellHeaderMallSearchContainer}>
              <TableCellHeader
                colSpan={numberOfColumns - btnNoOfColumns}
                className={`
                  ${tblClasses.tableCellHeader} ${tblClasses.colStatus}
                  ${tblClasses.tableHeaderTitle} ${tblClasses.noBordersTopBot}
                `}
              >
                <MallSearchInput
                  key="autobookingMallsTableSearch"
                  initialSearch={searchText}
                  placeholder="Search an SM Mall"
                  onSearchChange={onSearchText}
                  className={tblClasses.tableCellHeaderMallSearch}
                />
              </TableCellHeader>
              <TableCellHeader
                colSpan={btnNoOfColumns}
                className={`
                  ${tblClasses.tableCellHeader} ${tblClasses.colStatus}
                  ${tblClasses.tableHeaderTitle} ${tblClasses.noBordersTopBot}
                `}
                textAlign="right"
              >
                {!userType && (
                  <ButtonActions
                    id={"expressFleetBulkActions"}
                    btnText="Bulk Actions"
                    items={[
                      {
                        action: () => setOpenDisableModal(true),
                        id: "expressFleetBulkActionsDisableAutobooking",
                        label: "Deactivate Auto Booking"
                      },
                      {
                        action: () => setOpenEnableModal(true),
                        id: "expressFleetBulkActionsEnableAutobooking",
                        label: "Activate Auto Booking"
                      }
                    ]}
                  />
                )}
              </TableCellHeader>
            </TableRow>
            <TableRow>
              <TableCellHeader
                className={`
                  ${tblClasses.tableCellHeader} ${tblClasses.colStatus}
                  ${tblClasses.textBold} ${tblClasses.noBordersTopBot}
                `}
              >
                <FormattedMessage defaultMessage="Mall" description="mall" />
              </TableCellHeader>
              <TableCellHeader
                textAlign="center"
                className={`
                  ${tblClasses.tableCellHeader} ${tblClasses.colStatus}
                  ${tblClasses.textBold} ${tblClasses.noBordersTopBot}
                `}
              >
                <FormattedMessage
                  defaultMessage="Default Courier"
                  description="Default Courier"
                />
              </TableCellHeader>
              <TableCellHeader
                textAlign="center"
                className={`
                  ${tblClasses.tableCellHeader} ${tblClasses.colStatus}
                  ${tblClasses.textBold} ${tblClasses.noBordersTopBot}
                `}
              >
                <FormattedMessage
                  defaultMessage="Ready Status (mins)"
                  description="Ready Status Minutes"
                />
              </TableCellHeader>
              <TableCellHeader
                textAlign="center"
                className={`
                  ${tblClasses.tableCellHeader} ${tblClasses.colStatus}
                  ${tblClasses.textBold} ${tblClasses.noBordersTopBot}
                `}
              >
                <FormattedMessage
                  defaultMessage="Collected Status (mins)"
                  description="Collected Status Minutes"
                />
              </TableCellHeader>
              <TableCellHeader
                textAlign="center"
                className={`
                  ${tblClasses.tableCellHeader} ${tblClasses.colStatus}
                  ${tblClasses.textBold} ${tblClasses.noBordersTopBot}
                `}
              >
                <FormattedMessage
                  defaultMessage="Any Status (mins)"
                  description="Any Status Minutes"
                />
              </TableCellHeader>
              <TableCellHeader
                textAlign="center"
                className={`
                  ${tblClasses.tableCellHeader} ${tblClasses.colStatus}
                  ${tblClasses.textBold} ${tblClasses.noBordersTopBot}
                `}
              >
                <FormattedMessage
                  defaultMessage="Auto-booking"
                  description="Auto-booking"
                />
              </TableCellHeader>
              <TableCellHeader
                textAlign="center"
                className={`
                  ${tblClasses.tableCellHeader} ${tblClasses.colStatus}
                  ${tblClasses.textBold} ${tblClasses.noBordersTopBot}
                `}
              >
                <FormattedMessage
                  defaultMessage="Action"
                  description="Action"
                />
              </TableCellHeader>
            </TableRow>
          </TableHead>

          {!userType && (
            <TableFooter>
              <TableRow>
                <TablePagination
                  colSpan={numberOfColumns}
                  settings={settings}
                  hasNextPage={
                    pageInfo && !disabled ? pageInfo.hasNextPage : false
                  }
                  onNextPage={onNextPage}
                  onUpdateListSettings={onUpdateListSettings}
                  hasPreviousPage={
                    pageInfo && !disabled ? pageInfo.hasPreviousPage : false
                  }
                  onPreviousPage={onPreviousPage}
                />
              </TableRow>
            </TableFooter>
          )}

          <TableBody>
            <TableBodyContent
              tblClasses={tblClasses}
              numberOfColumns={numberOfColumns}
              isLoading={isLoading}
              dataList={mallList}
              refetch={refetch}
              setOpenModal={setOpenModal}
              selectedMall={selectedMall}
              setSelectedMall={setSelectedMall}
              activeTab={activeTab}
            />
          </TableBody>
        </Table>
      </Card>

      <AutoBookingDialog
        mall={selectedMall}
        open={openModal}
        onClose={closeModal}
        refetch={refetch}
        activeTab={activeTab}
      />

      <MallsDialog
        open={openEnableModal}
        onClose={closeEnableModal}
        modalTitle={"Activate Auto Booking" + " - " + mallsDialogTitle}
        modalText="Only malls with configured timers are listed and available for activation."
        confirmLabel="ACTIVATE"
        confirmLabelBgColor="#0030FF"
        cancelLabel="CANCEL"
        filterAutoBooking={{
          isActive: false,
          storeType
        }}
        handleConfirm={handleConfirmEnableAutobooking}
        handleCancel={closeEnableModal}
      />
      <MallsDialog
        open={openDisableModal}
        onClose={closeDisableModal}
        modalTitle={"Deactivate Auto Booking" + " - " + mallsDialogTitle}
        modalText="Only malls with configured timers are listed and available for deactivation."
        confirmLabel="DEACTIVATE"
        confirmLabelBgColor="#EC5D77"
        cancelLabel="CANCEL"
        filterAutoBooking={{
          isActive: true,
          storeType
        }}
        handleConfirm={handleConfirmDisableAutobooking}
        handleCancel={closeDisableModal}
      />
    </>
  );
};

export default ExpressFleetMallsTable;
